// src/components/aiinsight/AiChartRenderer.jsx
// Renders charts based on the viz_config_json from the backend.
// Supports 7 chart categories with 15+ chart types using Recharts.

import React, { useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell,
    LineChart, Line, Area, AreaChart,
    ComposedChart,
    ScatterChart, Scatter, ZAxis,
    FunnelChart, Funnel, LabelList,
    Treemap,
} from 'recharts';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Typography, Chip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const CHART_COLORS = [
    '#6366f1', '#22d3ee', '#f59e0b', '#10b981', '#f43f5e',
    '#a78bfa', '#34d399', '#fb923c', '#38bdf8', '#e879f9',
    '#fbbf24', '#4ade80', '#f87171', '#60a5fa', '#c084fc',
];

const AXIS_TICK  = '#94a3b8';
const AXIS_LINE  = 'rgba(148,163,184,0.2)';
const GRID_LINE  = 'rgba(148,163,184,0.15)';
const TT_BG     = '#1e293b';
const TT_BORDER = 'rgba(99,102,241,0.3)';
const TT_TEXT   = '#e2e8f0';

// ── Compact number formatter ────────────────────────────────────────────────
const compactNum = (raw, forceDollar = false) => {
    if (raw === null || raw === undefined || raw === '') return '—';
    try {
        const valStr = String(raw);
        const hasDollar = forceDollar || valStr.startsWith('$');
        const hasPercent = valStr.endsWith('%');
        const str = valStr.replace(/[$,]/g, '');
        const n = parseFloat(str);
        if (isNaN(n)) return valStr;
        let formatted;
        if (Math.abs(n) >= 1e9)      formatted = `${(n / 1e9).toFixed(1)}B`;
        else if (Math.abs(n) >= 1e6) formatted = `${(n / 1e6).toFixed(1)}M`;
        else if (Math.abs(n) >= 1e3) formatted = `${(n / 1e3).toFixed(1)}K`;
        else                         formatted = n % 1 === 0 ? String(n) : n.toFixed(2);
        return (hasDollar ? '$' : '') + formatted + (hasPercent ? '%' : '');
    } catch (e) {
        return String(raw);
    }
};

// ── Format numeric values based on column context ─────────────────────────────
const formatNumericValue = (value, fieldName = '') => {
    try {
        if (value === null || value === undefined || value === '') return value;
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        const fn = String(fieldName || '').toLowerCase();
        if (fn === 'year' || fn.endsWith('_year') || fn.endsWith(' year')) return String(value);
        const isCurrency = fn.match(/(amount|payment|revenue|cost|price|fee|balance|total_pay|reimbursement|billing|spend|budget)/);
        if (isCurrency) {
            return compactNum(num, true);
        }
        return num % 1 === 0 ? num.toLocaleString() : num.toFixed(2);
    } catch (e) {
        return String(value);
    }
};
const CURSOR     = 'rgba(99,102,241,0.1)';

const tooltipStyle = { background: TT_BG, border: `1px solid ${TT_BORDER}`, borderRadius: 8, color: TT_TEXT };
const legendStyle  = { fontSize: 12, color: AXIS_TICK };
const axisProps    = { tick: { fontSize: 11, fill: AXIS_TICK }, axisLine: { stroke: AXIS_LINE }, tickLine: false };
const gridProps    = { strokeDasharray: '3 3', stroke: GRID_LINE };

// ── helpers ──────────────────────────────────────────────────────────────────
const yArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);

// ── ID-like column detector (mirrors backend logic) ─────────────────────────
const ID_PATTERN = /(key|id|code|num|number|index|seq)$/i;
const isIdLikeCol = (colName, dataArr) => {
    if (ID_PATTERN.test(colName)) return true;
    // Pure-integer column heuristic
    if (dataArr && dataArr.length > 0) {
        return dataArr.every(r => r[colName] == null || Number.isInteger(r[colName]));
    }
    return false;
};

// ── Smart axis resolver ────────────────────────────────────────────────────
// Called only when vizConfig is missing x_axis / y_axis.
const resolveAxes = (vizConfig, dataArr) => {
    if (!dataArr || !dataArr.length) return { xKey: null, yKeys: [] };
    const cols = Object.keys(dataArr[0]);
    const isNumCol = (k) => {
        for (let i = 0; i < Math.min(dataArr.length, 10); i++) {
            const v = dataArr[i][k];
            if (typeof v === 'number' && !isNaN(v)) return true;
            if (v !== null && v !== undefined) return false;
        }
        return false;
    };

    const numericCols = cols.filter(isNumCol);
    const idCols = numericCols.filter(k => isIdLikeCol(k, dataArr));
    const metricCols = numericCols.filter(k => !idCols.includes(k));
    const catCols = cols.filter(k => !numericCols.includes(k));

    const xKey = vizConfig?.x_axis
        || catCols[0]
        || idCols[0]
        || cols[0];

    const yCandidates = yArr(vizConfig?.y_axis).length > 0
        ? yArr(vizConfig.y_axis)
        : metricCols.length > 0
            ? metricCols
            : numericCols.filter(k => k !== xKey);

    return { xKey, yKeys: yCandidates.length > 0 ? yCandidates : [cols[cols.length - 1]] };
};

const AiChartRenderer = ({ vizConfig, data, height = 280, animationDisabled = false }) => {
    const columns = useMemo(() => {
        if (!data || !data.length || !data[0]) return [];
        return Object.keys(data[0]).map((key) => ({
            field: key,
            headerName: String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
            flex: 1, minWidth: 100,
            renderCell: (params) => (
                <span style={{ fontSize: 11 }}>
                    {formatNumericValue(params?.value, key)}
                </span>
            )
        }));
    }, [data]);

    const rows = useMemo(() => (data || []).map((r, i) => ({ id: i, ...r })), [data]);

    if (!data || !data.length) {
        return (
            <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No data to display.</Typography>
            </Box>
        );
    }

    const H = height;
    
    // ── Chart Type Normalization ─────────────────────────────────────────────
    const normalizedType = useMemo(() => {
        let t = (vizConfig?.type || '').toLowerCase().trim().replace(/\s+/g, '_');
        const mapping = {
            'bar': 'bar_chart',
            'line': 'line_chart',
            'pie': 'pie_chart',
            'donut': 'donut_chart',
            'area': 'area_chart',
            'scatter': 'scatter_chart',
            'kpi': 'kpi_card',
            'spark': 'sparkline',
            'groupedbar': 'grouped_bar',
            'stackedbar': 'stacked_bar',
            'treemap': 'treemap_chart',
            'funnel': 'funnel_chart',
            'sankey': 'sankey_chart',
            'heatmap': 'heatmap_chart',
            'waterfall': 'waterfall_chart',
            'auto': 'bar_chart',
            'graph': 'bar_chart',
            'chart': 'bar_chart',
        };
        return mapping[t] || t;
    }, [vizConfig?.type]);

    // For time-series charts, sort data chronologically by x_axis so the line is never scrambled
    const TIME_SERIES_TYPES = ['line_chart', 'area_chart', 'combo_chart', 'sparkline'];
    const sortedData = useMemo(() => {
        if (!TIME_SERIES_TYPES.includes(normalizedType)) return data;
        const xKey = vizConfig?.x_axis;
        if (!xKey || !data?.length) return data;
        
        return [...data].sort((a, b) => {
            const va = a[xKey], vb = b[xKey];
            
            // Handle YearMonth composite (e.g., "2023-06")
            if (xKey === 'YearMonth' && typeof va === 'string' && typeof vb === 'string') {
                return va.localeCompare(vb); // ISO format sorts correctly
            }
            
            // Handle Year+Month separate columns for backward compatibility
            if (xKey === 'Month' && a.Year !== undefined && b.Year !== undefined) {
                // Sort by Year first, then Month
                if (a.Year !== b.Year) {
                    return a.Year - b.Year;
                }
                return a.Month - b.Month;
            }
            
            // Handle Date objects (from backend normalization)
            if (xKey === 'Date' && typeof va === 'string' && typeof vb === 'string') {
                return va.localeCompare(vb); // ISO date strings sort correctly
            }
            
            // Original logic for other time columns
            const da = new Date(va), db = new Date(vb);
            if (!isNaN(da) && !isNaN(db)) return da - db;
            return String(va).localeCompare(String(vb));
        });
    }, [data, normalizedType, vizConfig?.x_axis, vizConfig?.type]);


    const isCurrency = useMemo(() => {
        const ys = yArr(vizConfig?.y_axis);
        // If y_axis is explicitly configured, check those; otherwise scan all numeric column names
        const toCheck = ys.length > 0
            ? ys
            : (data && data.length > 0
                ? Object.keys(data[0]).filter(k => {
                    for (let i = 0; i < Math.min(data.length, 10); i++) {
                        if (typeof data[i][k] === 'number') return true;
                        if (data[i][k] !== null && data[i][k] !== undefined) return false;
                    }
                    return false;
                })
                : []);
        return toCheck.some(y => String(y).toLowerCase().match(/(amount|payment|revenue|cost|price|fee|balance|total_pay|reimbursement|billing|spend|budget)/));
    }, [vizConfig?.y_axis, data]);

    switch (normalizedType) {
        // ── TRENDS ────────────────────────────────────────────────────────────

        case 'line_chart': {
            const { xKey, yKeys } = resolveAxes(vizConfig, data);
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff' }}>
                    <ResponsiveContainer width="100%" height={H}>
                        <LineChart data={sortedData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis dataKey={xKey} {...axisProps} />
                            <YAxis {...axisProps} tickFormatter={v => compactNum(v, isCurrency)} />
                            <Tooltip contentStyle={tooltipStyle} formatter={v => compactNum(v, isCurrency)} />
                            <Legend wrapperStyle={legendStyle} />
                            {yKeys.map((y, i) => (
                                <Line key={y} type="monotone" dataKey={y}
                                    stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2.5}
                                    dot={{ r: 4, fill: CHART_COLORS[i % CHART_COLORS.length] }} activeDot={{ r: 6 }}
                                    isAnimationActive={!animationDisabled} />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        case 'area_chart': {
            const { xKey, yKeys } = resolveAxes(vizConfig, data);
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff' }}>
                    <ResponsiveContainer width="100%" height={H}>
                        <AreaChart data={sortedData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <defs>
                                {yKeys.map((y, i) => (
                                    <linearGradient key={y} id={`grad_${i}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.25} />
                                        <stop offset="95%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.02} />
                                    </linearGradient>
                                ))}
                            </defs>
                            <CartesianGrid {...gridProps} />
                            <XAxis dataKey={xKey} {...axisProps} />
                            <YAxis {...axisProps} tickFormatter={v => compactNum(v, isCurrency)} />
                            <Tooltip contentStyle={tooltipStyle} formatter={v => compactNum(v, isCurrency)} />
                            <Legend wrapperStyle={legendStyle} />
                            {yKeys.map((y, i) => (
                                <Area key={y} type="monotone" dataKey={y}
                                    stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2}
                                    fill={`url(#grad_${i})`}
                                    isAnimationActive={!animationDisabled} />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        case 'combo_chart': {
            const { xKey, yKeys } = resolveAxes(vizConfig, data);
            // First metric → bars, rest → lines
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff' }}>
                    <ResponsiveContainer width="100%" height={H}>
                        <ComposedChart data={sortedData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis dataKey={xKey} {...axisProps} />
                            <YAxis {...axisProps} tickFormatter={v => compactNum(v, isCurrency)} />
                            <Tooltip contentStyle={tooltipStyle} formatter={v => compactNum(v, isCurrency)} />
                            <Legend wrapperStyle={legendStyle} />
                            {yKeys.map((y, i) =>
                                i === 0
                                    ? <Bar key={y} dataKey={y} fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} opacity={0.85} isAnimationActive={!animationDisabled} />
                                    : <Line key={y} type="monotone" dataKey={y}
                                        stroke={CHART_COLORS[i]} strokeWidth={2.5}
                                        dot={{ r: 4 }} activeDot={{ r: 6 }}
                                        isAnimationActive={!animationDisabled} />
                            )}
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        // ── COMPARE & RANK ────────────────────────────────────────────────────

        case 'bar_chart': {
            const { xKey, yKeys } = resolveAxes(vizConfig, data);
            // If x-axis is an ID/key column, coerce values to strings so Recharts
            // treats them as discrete categories (not a numeric scale 0-9)
            const coerceNeeded = xKey && isIdLikeCol(xKey, data);
            const chartData = coerceNeeded
                ? data.map(r => ({ ...r, [xKey]: String(r[xKey]) }))
                : data;
            // Determine if x-axis labels are long (need rotation)
            const maxLabelLen = chartData.reduce((max, r) => Math.max(max, String(r[xKey] ?? '').length), 0);
            const needsAngle = maxLabelLen > 8 || chartData.length > 6;
            const bottomMargin = needsAngle ? Math.min(90, Math.max(40, maxLabelLen * 4)) : 10;
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff' }}>
                    <ResponsiveContainer width="100%" height={H + (needsAngle ? bottomMargin : 0)}>
                        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: needsAngle ? bottomMargin : 10 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis
                                dataKey={xKey}
                                {...axisProps}
                                interval={0}
                                angle={needsAngle ? -35 : 0}
                                textAnchor={needsAngle ? 'end' : 'middle'}
                                height={needsAngle ? bottomMargin + 10 : 30}
                                tick={{ fontSize: Math.max(9, Math.min(11, 110 / Math.max(chartData.length, 1))), fill: AXIS_TICK }}
                            />
                            <YAxis {...axisProps} tickFormatter={v => compactNum(v, isCurrency)} />
                            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CURSOR }} formatter={v => [compactNum(v, isCurrency)]} />
                            <Legend wrapperStyle={legendStyle} />
                            {yKeys.map((y, i) => (
                                <Bar key={y} dataKey={y} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} isAnimationActive={!animationDisabled} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        case 'grouped_bar': {
            const { xKey, yKeys } = resolveAxes(vizConfig, data);
            const maxLabelLen2 = data.reduce((max, r) => Math.max(max, String(r[xKey] ?? '').length), 0);
            const needsAngle2 = maxLabelLen2 > 8 || data.length > 6;
            const bottomMargin2 = needsAngle2 ? Math.min(90, Math.max(40, maxLabelLen2 * 4)) : 10;
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff' }}>
                    <ResponsiveContainer width="100%" height={H + (needsAngle2 ? bottomMargin2 : 0)}>
                        <BarChart data={data} barGap={2} margin={{ top: 5, right: 20, left: 0, bottom: needsAngle2 ? bottomMargin2 : 5 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis
                                dataKey={xKey}
                                {...axisProps}
                                interval={0}
                                angle={needsAngle2 ? -35 : 0}
                                textAnchor={needsAngle2 ? 'end' : 'middle'}
                                height={needsAngle2 ? bottomMargin2 + 10 : 30}
                                tick={{ fontSize: Math.max(9, Math.min(11, 110 / Math.max(data.length, 1))), fill: AXIS_TICK }}
                            />
                            <YAxis {...axisProps} tickFormatter={v => compactNum(v, isCurrency)} />
                            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CURSOR }} />
                            <Legend wrapperStyle={legendStyle} />
                            {yKeys.map((y, i) => (
                                <Bar key={y} dataKey={y} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[3, 3, 0, 0]} isAnimationActive={!animationDisabled} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        case 'stacked_bar': {
            const { xKey, yKeys } = resolveAxes(vizConfig, data);
            const maxLabelLen3 = data.reduce((max, r) => Math.max(max, String(r[xKey] ?? '').length), 0);
            const needsAngle3 = maxLabelLen3 > 8 || data.length > 6;
            const bottomMargin3 = needsAngle3 ? Math.min(90, Math.max(40, maxLabelLen3 * 4)) : 10;
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff' }}>
                    <ResponsiveContainer width="100%" height={H + (needsAngle3 ? bottomMargin3 : 0)}>
                        <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: needsAngle3 ? bottomMargin3 : 5 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis
                                dataKey={xKey}
                                {...axisProps}
                                interval={0}
                                angle={needsAngle3 ? -35 : 0}
                                textAnchor={needsAngle3 ? 'end' : 'middle'}
                                height={needsAngle3 ? bottomMargin3 + 10 : 30}
                                tick={{ fontSize: Math.max(9, Math.min(11, 110 / Math.max(data.length, 1))), fill: AXIS_TICK }}
                            />
                            <YAxis {...axisProps} tickFormatter={v => compactNum(v, isCurrency)} />
                            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CURSOR }} />
                            <Legend wrapperStyle={legendStyle} />
                            {yKeys.map((y, i) => (
                                <Bar key={y} dataKey={y} stackId="s1"
                                    fill={CHART_COLORS[i % CHART_COLORS.length]}
                                    radius={i === yKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                    isAnimationActive={!animationDisabled} />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        case 'waterfall_chart': {
            // Recharts doesn't have native waterfall; we simulate with stacked bars
            const valueKey = yArr(vizConfig.y_axis)[0] || '';
            const catKey = vizConfig.x_axis || '';
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff' }}>
                    <ResponsiveContainer width="100%" height={H}>
                        <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis dataKey={catKey} {...axisProps} />
                            <YAxis {...axisProps} tickFormatter={v => compactNum(v, isCurrency)} />
                            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CURSOR }} />
                            <Legend wrapperStyle={legendStyle} />
                            <Bar dataKey={valueKey} radius={[4, 4, 0, 0]}>
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`}
                                        fill={entry[valueKey] >= 0 ? '#10b981' : '#f43f5e'} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        // ── COMPOSITION ───────────────────────────────────────────────────────

        case 'donut_chart': {
            // y_axis may be a string OR an array (from LLM) — always extract scalar
            const rawY = vizConfig.y_axis ?? vizConfig.value_key;
            const valueKey = Array.isArray(rawY) ? rawY[0] : rawY;
            const nameKey  = vizConfig.x_axis  ?? vizConfig.name_key;
            // Auto-resolve if still missing
            const { xKey: axX, yKeys: axY } = resolveAxes(vizConfig, data);
            const finalValueKey = valueKey || axY[0];
            const finalNameKey  = nameKey  || axX;
            const PIE_H = Math.max(H, 300);
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff', paddingBottom: 15 }}>
                    <ResponsiveContainer width="100%" height={PIE_H}>
                        <PieChart margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
                            <Pie data={data} dataKey={finalValueKey} nameKey={finalNameKey}
                                cx="50%" cy="46%" outerRadius={100} innerRadius={55}
                                paddingAngle={3}
                                isAnimationActive={false}
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                labelLine={{ stroke: AXIS_TICK, strokeWidth: 1 }}>
                                {data.map((_, i) => (
                                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip contentStyle={tooltipStyle} formatter={v => compactNum(v, isCurrency)} />
                            <Legend wrapperStyle={legendStyle} verticalAlign="bottom" />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        case 'pie_chart': {
            // y_axis may be a string OR an array (from LLM) — always extract scalar
            const rawY = vizConfig.y_axis ?? vizConfig.value_key;
            const valueKey = Array.isArray(rawY) ? rawY[0] : rawY;
            const nameKey  = vizConfig.x_axis  ?? vizConfig.name_key;
            // Auto-resolve if still missing
            const { xKey: axXp, yKeys: axYp } = resolveAxes(vizConfig, data);
            const finalValueKey = valueKey || axYp[0];
            const finalNameKey  = nameKey  || axXp;
            const PIE_H2 = Math.max(H, 300);
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff', paddingBottom: 15 }}>
                    <ResponsiveContainer width="100%" height={PIE_H2}>
                        <PieChart margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
                            <Pie data={data} dataKey={finalValueKey} nameKey={finalNameKey}
                                cx="50%" cy="46%" outerRadius={105}
                                paddingAngle={2}
                                isAnimationActive={false}
                                label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                                labelLine={{ stroke: AXIS_TICK, strokeWidth: 1 }}>
                                {data.map((_, i) => (
                                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip contentStyle={tooltipStyle} formatter={v => [compactNum(v, isCurrency), finalValueKey]} />
                            <Legend wrapperStyle={legendStyle} verticalAlign="bottom" />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        case 'treemap_chart': {
            const valueKey = yArr(vizConfig.y_axis)[0] || vizConfig.value_key;
            const nameKey  = vizConfig.x_axis || vizConfig.name_key || 'name';
            const treeData = data.map((d, i) => ({
                name: d[nameKey] ?? `Item ${i + 1}`,
                size: Number(d[valueKey]) || 0,
                fill: CHART_COLORS[i % CHART_COLORS.length],
            }));
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff' }}>
                    <ResponsiveContainer width="100%" height={H}>
                        <Treemap data={treeData} dataKey="size" nameKey="name"
                            aspectRatio={4 / 3}
                            content={({ x, y, width, height, name, fill }) => (
                                <g>
                                    <rect x={x} y={y} width={width} height={height}
                                        fill={fill} stroke="#fff" strokeWidth={2} rx={4} />
                                    {width > 50 && height > 28 && (
                                        <text x={x + width / 2} y={y + height / 2}
                                            textAnchor="middle" dominantBaseline="middle"
                                            fill="#fff" fontSize={Math.min(12, width / 8)} fontWeight={600}>
                                            {name}
                                        </text>
                                    )}
                                </g>
                            )}
                        />
                    </ResponsiveContainer>
                </div>
            );
        }

        // ── PERFORMANCE ───────────────────────────────────────────────────────

        case 'kpi_card': {
            const { xKey, yKeys } = resolveAxes(vizConfig, data);
            const valueKey = vizConfig.value_key || yArr(vizConfig.y_axis)[0] || yKeys[0];
            const xLabelKey = vizConfig.x_axis || xKey || Object.keys(data[0] || {})[0];
            const val = valueKey && data[0] ? data[0][valueKey] : null;
            const label = vizConfig.title || valueKey || 'KPI';
            return (
                <Box data-ai-chart="true" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: H, gap: 1 }}>
                    <TrendingUpIcon sx={{ fontSize: 32, color: '#6366f1', opacity: 0.7 }} />
                    <Typography variant="h2" sx={{ fontWeight: 900, color: '#6366f1', lineHeight: 1 }}>
                        {val !== null && val !== undefined
                            ? compactNum(val, isCurrency)
                            : '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {label}
                    </Typography>
                    {data.length > 1 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, justifyContent: 'center' }}>
                            {data.slice(0, 6).map((d, i) => (
                                <Chip key={i} label={`${d[xLabelKey]}: ${formatNumericValue(d[valueKey], valueKey)}`}
                                    size="small" sx={{ fontSize: 10, bgcolor: 'rgba(99,102,241,0.1)', color: '#818cf8' }} />
                            ))}
                        </Box>
                    )}
                </Box>
            );
        }

        case 'sparkline': {
            const { xKey, yKeys } = resolveAxes(vizConfig, data);
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff' }}>
                    <ResponsiveContainer width="100%" height={H}>
                        <LineChart data={sortedData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                            <XAxis dataKey={xKey} hide />
                            <YAxis hide />
                            <Tooltip contentStyle={tooltipStyle} />
                            {yKeys.map((y, i) => (
                                <Line key={y} type="monotone" dataKey={y}
                                    stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={3}
                                    dot={false} activeDot={{ r: 5 }}
                                    isAnimationActive={!animationDisabled} />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        // ── RELATIONS ─────────────────────────────────────────────────────────

        case 'scatter_chart': {
            const { xKey, yKeys } = resolveAxes(vizConfig, data);
            const yKey = vizConfig.y_axis ? yArr(vizConfig.y_axis)[0] : yKeys[0];
            const isCatX = data.length > 0 && typeof data[0][xKey] === 'string';
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff' }}>
                    <ResponsiveContainer width="100%" height={H}>
                        <ScatterChart margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis type={isCatX ? "category" : "number"} dataKey={xKey} name={xKey} {...axisProps} />
                            <YAxis type="number" dataKey={yKey} name={yKey} {...axisProps} tickFormatter={v => compactNum(v, isCurrency)} />
                            <Tooltip contentStyle={tooltipStyle} cursor={{ strokeDasharray: '3 3' }} formatter={v => compactNum(v, isCurrency)} />
                            <Scatter data={data} fill={CHART_COLORS[0]} opacity={0.75} />
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        case 'bubble_chart': {
            const { xKey, yKeys } = resolveAxes(vizConfig, data);
            const yKey = vizConfig.y_axis ? yArr(vizConfig.y_axis)[0] : yKeys[0];
            const sKey = vizConfig.size_key || (yKeys.length > 1 ? yKeys[1] : yKey);
            const isCatX = data.length > 0 && typeof data[0][xKey] === 'string';
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff' }}>
                    <ResponsiveContainer width="100%" height={H}>
                        <ScatterChart margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <CartesianGrid {...gridProps} />
                            <XAxis type={isCatX ? "category" : "number"} dataKey={xKey} name={xKey} {...axisProps} />
                            <YAxis type="number" dataKey={yKey} name={yKey} {...axisProps} tickFormatter={v => compactNum(v, isCurrency)} />
                            <ZAxis type="number" dataKey={sKey} range={[40, 400]} />
                            <Tooltip contentStyle={tooltipStyle} formatter={v => compactNum(v, isCurrency)} />
                            <Scatter data={data} fill={CHART_COLORS[0]} opacity={0.7} />
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        // ── FLOW ──────────────────────────────────────────────────────────────

        case 'funnel_chart': {
            const nameKey  = vizConfig.x_axis || 'stage';
            const valueKey = yArr(vizConfig.y_axis)[0] || 'value';
            const funnelData = data.map((d, i) => ({
                name: d[nameKey],
                value: d[valueKey],
                fill: CHART_COLORS[i % CHART_COLORS.length],
            }));
            return (
                <div data-ai-chart="true" style={{ background: '#ffffff' }}>
                    <ResponsiveContainer width="100%" height={H}>
                        <FunnelChart>
                            <Tooltip contentStyle={tooltipStyle} />
                            <Funnel dataKey="value" data={funnelData} isAnimationActive={!animationDisabled}>
                                <LabelList position="right" fill={AXIS_TICK} stroke="none"
                                    dataKey="name" style={{ fontSize: 11 }} />
                            </Funnel>
                        </FunnelChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        // ── HEATMAP (simulated with a grid of colored cells) ──────────────────
        case 'heatmap_chart': {
            const xKey = vizConfig.x_axis;
            const yKey = vizConfig.y_axis;
            const vKey = vizConfig.value_key || yArr(vizConfig.y_axis)[0];
            const xVals = [...new Set(data.map(d => d[xKey]))];
            const yVals = [...new Set(data.map(d => d[yKey]))];
            const vals  = data.map(d => Number(d[vKey]) || 0);
            const minV  = Math.min(...vals);
            const maxV  = Math.max(...vals);
            const norm  = (v) => maxV === minV ? 0.5 : (v - minV) / (maxV - minV);
            const cellW = Math.max(28, Math.floor(260 / xVals.length));
            const cellH = Math.max(20, Math.floor((H - 40) / yVals.length));

            const getCell = (x, y) => data.find(d => d[xKey] === x && d[yKey] === y);

            return (
                <Box data-ai-chart="true" sx={{ overflowX: 'auto', pt: 1 }}>
                    <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <thead>
                            <tr>
                                <td style={{ width: 60 }} />
                                {xVals.map(x => (
                                    <td key={x} style={{ textAlign: 'center', fontSize: 10, color: AXIS_TICK, padding: '2px 4px', width: cellW }}>
                                        {x}
                                    </td>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {yVals.map(y => (
                                <tr key={y}>
                                    <td style={{ fontSize: 10, color: AXIS_TICK, paddingRight: 6, whiteSpace: 'nowrap' }}>{y}</td>
                                    {xVals.map(x => {
                                        const cell = getCell(x, y);
                                        const v = cell ? Number(cell[vKey]) || 0 : 0;
                                        const n = norm(v);
                                        const bg = `rgba(99,102,241,${0.08 + n * 0.85})`;
                                        return (
                                            <td key={x} title={`${x} / ${y}: ${v}`} style={{
                                                background: bg, height: cellH, width: cellW,
                                                textAlign: 'center', fontSize: 9,
                                                color: n > 0.55 ? '#fff' : '#475569',
                                                border: '1px solid rgba(255,255,255,0.08)',
                                            }}>
                                                {v !== 0 ? v : ''}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Box>
            );
        }

        // ── TABLE (default / DETAILS) ─────────────────────────────────────────
        case 'table':
        default:
            if (animationDisabled) {
                // PDF/Export static mode: Unroll completely native HTML table
                return (
                    <Box sx={{ width: '100%', breakInside: 'avoid' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, border: '1px solid rgba(99,102,241,0.2)' }}>
                            <thead style={{ background: 'rgba(99,102,241,0.1)' }}>
                                <tr>
                                    {columns.map(c => (
                                        <th key={c.field} style={{ padding: '8px 12px', fontWeight: 600, color: '#334155', borderBottom: '1px solid rgba(99,102,241,0.2)', textAlign: 'left' }}>
                                            {c.headerName}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={r.id} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                        {columns.map(c => (
                                            <td key={c.field} style={{ padding: '6px 12px', color: '#475569', borderBottom: '1px solid rgba(226,232,240,0.5)' }}>
                                                {c.renderCell ? c.renderCell({ value: r[c.field] }) : r[c.field]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                );
            }
            
            // Live Editor mode: Paginated Mui DataGrid
            return (
                <Box sx={{ height: H }}>
                    <DataGrid
                        rows={rows} columns={columns} density="compact"
                        disableRowSelectionOnClick hideFooterPagination={rows.length <= 5}
                        pageSizeOptions={[5]}
                        initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                        sx={{
                            border: '1px solid rgba(99,102,241,0.2)', borderRadius: 2, fontSize: 12,
                            '& .MuiDataGrid-columnHeaders': { background: 'rgba(99,102,241,0.1)' },
                            '& .MuiDataGrid-cell': { borderColor: 'rgba(255,255,255,0.05)' },
                        }}
                    />
                </Box>
            );
    }
};

export default AiChartRenderer;
